/**
 * Zeus Help — a psychologist-style guided interview that helps the user craft
 * their Identity statement, Affirmations, and Gratefuls in present-tense
 * "I AM" form. Opened from the Identity, Affirmations, and Gratefuls pages.
 *
 * Chats with POST /api/zeus/identity-interview; live "proposals" returned by
 * Zeus render as cards the user can apply individually:
 *   identity      → PUT  /api/incantation           (replaces the statement)
 *   affirmations  → POST /api/intentions   { text }
 *   gratefuls     → POST /api/gratefuls    { content }
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Send, Sparkles, Zap, Check, Plus } from "lucide-react";

type Focus = "identity" | "affirmations" | "gratefuls";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface Proposals {
  identity?: string;
  affirmations?: string[];
  gratefuls?: string[];
}

const FOCUS_LABEL: Record<Focus, string> = {
  identity: "Identity",
  affirmations: "Affirmations",
  gratefuls: "Gratefuls",
};

export function ZeusHelpButton({ focus }: { focus: Focus }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`button-zeus-help-${focus}`}
      >
        <Zap className="w-4 h-4 mr-2 text-primary" />
        Zeus Help
      </Button>
      {open && <ZeusHelpDialog focus={focus} open={open} onOpenChange={setOpen} />}
    </>
  );
}

function ZeusHelpDialog({
  focus,
  open,
  onOpenChange,
}: {
  focus: Focus;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [proposals, setProposals] = useState<Proposals>({});
  const [applied, setApplied] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, proposals]);

  const chatMutation = useMutation({
    mutationFn: async (payload: { message: string; conversationHistory: ChatMessage[] }) => {
      const res = await apiRequest("POST", "/api/zeus/identity-interview", {
        ...payload,
        focus,
      });
      return (await res.json()) as { response: string; proposals: Proposals | null };
    },
    onSuccess: (data) => {
      setMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
      if (data.proposals) {
        setProposals((prev) => ({ ...prev, ...data.proposals }));
      }
    },
    onError: (err: any) => {
      toast({
        title: "Zeus couldn't respond",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    },
  });

  // Auto-start the interview when the dialog opens.
  useEffect(() => {
    if (open && !startedRef.current) {
      startedRef.current = true;
      chatMutation.mutate({
        message: `I'd like help writing my ${FOCUS_LABEL[focus].toLowerCase()}. Please interview me.`,
        conversationHistory: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = () => {
    const value = input.trim();
    if (!value || chatMutation.isPending) return;
    const next = [...messages, { role: "user" as const, content: value }];
    setMessages(next);
    setInput("");
    chatMutation.mutate({ message: value, conversationHistory: messages });
  };

  const applyIdentity = useMutation({
    mutationFn: async (content: string) => {
      await apiRequest("PUT", "/api/incantation", { content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/incantation"] });
      setApplied((prev) => new Set(prev).add("identity"));
      toast({ title: "Identity updated", description: "Your Identity statement has been replaced." });
    },
    onError: (err: any) =>
      toast({ title: "Failed to save Identity", description: err?.message ?? String(err), variant: "destructive" }),
  });

  const applyAffirmation = useMutation({
    mutationFn: async (text: string) => {
      await apiRequest("POST", "/api/intentions", { text });
      return text;
    },
    onSuccess: (text) => {
      queryClient.invalidateQueries({ queryKey: ["/api/intentions"] });
      setApplied((prev) => new Set(prev).add(`aff:${text}`));
      toast({ title: "Affirmation added" });
    },
    onError: (err: any) =>
      toast({ title: "Failed to add affirmation", description: err?.message ?? String(err), variant: "destructive" }),
  });

  const applyGrateful = useMutation({
    mutationFn: async (content: string) => {
      await apiRequest("POST", "/api/gratefuls", { content });
      return content;
    },
    onSuccess: (content) => {
      queryClient.invalidateQueries({ queryKey: ["/api/gratefuls"] });
      setApplied((prev) => new Set(prev).add(`grat:${content}`));
      toast({ title: "Grateful added" });
    },
    onError: (err: any) =>
      toast({ title: "Failed to add grateful", description: err?.message ?? String(err), variant: "destructive" }),
  });

  const hasProposals =
    Boolean(proposals.identity) ||
    Boolean(proposals.affirmations?.length) ||
    Boolean(proposals.gratefuls?.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Zeus Help — {FOCUS_LABEL[focus]}
          </DialogTitle>
          <DialogDescription>
            Zeus interviews you like a coach and turns your answers into present-tense "I AM"
            statements you can apply with one click. Skip anything, stop anytime.
          </DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`zeus-help-message-${m.role}-${i}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {chatMutation.isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Zeus is thinking…
            </div>
          )}

          {hasProposals && (
            <Card className="border-primary/40">
              <CardContent className="pt-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Sparkles className="w-4 h-4 text-primary" /> Zeus's suggestions — apply what feels true
                </div>

                {proposals.identity && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Identity statement</div>
                    <p className="text-sm whitespace-pre-wrap rounded-md bg-muted p-3">{proposals.identity}</p>
                    <Button
                      size="sm"
                      disabled={applied.has("identity") || applyIdentity.isPending}
                      onClick={() => applyIdentity.mutate(proposals.identity!)}
                      data-testid="button-apply-identity"
                    >
                      {applied.has("identity") ? (
                        <>
                          <Check className="w-4 h-4 mr-1" /> Applied
                        </>
                      ) : (
                        "Replace my Identity with this"
                      )}
                    </Button>
                    {!applied.has("identity") && (
                      <p className="text-xs text-muted-foreground">
                        This replaces your current Identity statement.
                      </p>
                    )}
                  </div>
                )}

                {Boolean(proposals.affirmations?.length) && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Affirmations</div>
                    {proposals.affirmations!.map((text, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <p className="flex-1 text-sm rounded-md bg-muted p-2.5">{text}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          disabled={applied.has(`aff:${text}`) || applyAffirmation.isPending}
                          onClick={() => applyAffirmation.mutate(text)}
                          data-testid={`button-apply-affirmation-${i}`}
                        >
                          {applied.has(`aff:${text}`) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {Boolean(proposals.gratefuls?.length) && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Gratefuls</div>
                    {proposals.gratefuls!.map((text, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <p className="flex-1 text-sm rounded-md bg-muted p-2.5">{text}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          disabled={applied.has(`grat:${text}`) || applyGrateful.isPending}
                          onClick={() => applyGrateful.mutate(text)}
                          data-testid={`button-apply-grateful-${i}`}
                        >
                          {applied.has(`grat:${text}`) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="border-t px-4 py-3 flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Type your answer…"
            disabled={chatMutation.isPending}
            data-testid="input-zeus-help"
          />
          <Button onClick={send} disabled={!input.trim() || chatMutation.isPending} data-testid="button-zeus-help-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
